import { useState, useEffect } from 'react';
import { TopBar } from '../../components/feature/TopBar';
import { Sidebar } from '../../components/feature/Sidebar';

export default function SettingsPage() {
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [activeTab, setActiveTab] = useState('general');
  const [settings, setSettings] = useState({
    general: {
      deviceName: 'Fronius Symo 10.0-3-M',
      location: 'Santiago, Chile',
      timezone: 'America/Santiago',
      language: 'es',
      updateInterval: 5,
      autoRefresh: true
    },
    notifications: {
      emailAlerts: true,
      systemErrors: true,
      maintenanceReminders: true,
      performanceReports: true,
      lowPerformance: true,
      highTemperature: true,
      gridDisconnection: true
    },
    thresholds: {
      maxTemperature: 70,
      minEfficiency: 85,
      maxPowerImbalance: 15,
      lowVoltageThreshold: 220,
      highVoltageThreshold: 240
    },
    display: {
      theme: 'light',
      chartAnimations: true,
      compactView: false,
      showAdvancedMetrics: true,
      defaultDashboard: 'overview'
    },
    api: {
      endpoint: 'http://192.168.1.100/solar_api/v1/',
      timeout: 10,
      retryAttempts: 3,
      enableLogging: false
    }
  });
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  useEffect(() => {
    setLastUpdate(new Date());
  }, []);

  const handleSettingChange = (category: string, key: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category as keyof typeof prev],
        [key]: value
      }
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    // Simular guardado
    setTimeout(() => {
      setIsSaving(false);
      setSaveMessage('Configuración guardada correctamente');
      setTimeout(() => setSaveMessage(''), 3000);
    }, 1000);
  };

  const handleReset = () => {
    if (confirm('¿Estás seguro de que quieres restablecer toda la configuración?')) {
      // Restablecer a valores por defecto
      setSettings({
        general: {
          deviceName: 'Fronius Symo 10.0-3-M',
          location: 'Santiago, Chile',
          timezone: 'America/Santiago',
          language: 'es',
          updateInterval: 5,
          autoRefresh: true
        },
        notifications: {
          emailAlerts: true,
          systemErrors: true,
          maintenanceReminders: true,
          performanceReports: true,
          lowPerformance: true,
          highTemperature: true,
          gridDisconnection: true
        },
        thresholds: {
          maxTemperature: 70,
          minEfficiency: 85,
          maxPowerImbalance: 15,
          lowVoltageThreshold: 220,
          highVoltageThreshold: 240
        },
        display: {
          theme: 'light',
          chartAnimations: true,
          compactView: false,
          showAdvancedMetrics: true,
          defaultDashboard: 'overview'
        },
        api: {
          endpoint: 'http://192.168.1.100/solar_api/v1/',
          timeout: 10,
          retryAttempts: 3,
          enableLogging: false
        }
      });
      setSaveMessage('Configuración restablecida a valores por defecto');
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };

  const tabs = [
    { id: 'general', label: 'General', icon: 'ri-settings-line' },
    { id: 'notifications', label: 'Notificaciones', icon: 'ri-notification-line' },
    { id: 'thresholds', label: 'Umbrales', icon: 'ri-alarm-line' },
    { id: 'display', label: 'Visualización', icon: 'ri-palette-line' },
    { id: 'api', label: 'API', icon: 'ri-code-line' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 ml-[var(--sidebar-width)]">
        <TopBar title="Configuración" lastUpdate={lastUpdate} />

        <div className="p-6 space-y-6">

          {/* Hero Section */}
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-blue-900 to-indigo-900 shadow-xl p-8">
            <div className="absolute inset-0 opacity-20">
              <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1451187580459-43490279c0fa?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80')] bg-cover bg-center mix-blend-overlay"></div>
            </div>
            <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-6">
              <div>
                <h1 className="text-3xl font-bold text-white mb-2">Configuración del Sistema</h1>
                <p className="text-blue-100 max-w-xl">
                  Personaliza tu experiencia, gestiona notificaciones y ajusta los parámetros técnicos de tu instalación.
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleReset}
                  className="px-4 py-2 bg-white/10 backdrop-blur-md border border-white/20 text-white rounded-xl hover:bg-white/20 transition-colors text-sm font-medium"
                >
                  Restablecer
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="px-6 py-2 bg-white text-blue-900 rounded-xl hover:bg-blue-50 transition-colors text-sm font-bold shadow-lg shadow-blue-900/20 flex items-center gap-2"
                >
                  {isSaving ? (
                    <>
                      <div className="animate-spin w-4 h-4 border-2 border-blue-900 border-t-transparent rounded-full"></div>
                      <span>Guardando...</span>
                    </>
                  ) : (
                    <>
                      <i className="ri-save-3-line"></i>
                      <span>Guardar Cambios</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Mensaje de guardado */}
          {saveMessage && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 animate-fade-in-down">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-600">
                  <i className="ri-checkbox-circle-line text-xl"></i>
                </div>
                <span className="text-green-800 font-medium">{saveMessage}</span>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Navigation Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden sticky top-6">
                <div className="p-4 bg-gray-50/50 border-b border-gray-100">
                  <h2 className="font-bold text-gray-900">Menú</h2>
                </div>
                <nav className="p-2 space-y-1">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${activeTab === tab.id
                        ? 'bg-blue-50 text-blue-700 shadow-sm'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                    >
                      <i className={`${tab.icon} text-lg ${activeTab === tab.id ? 'text-blue-600' : 'text-gray-400'}`}></i>
                      <span>{tab.label}</span>
                      {activeTab === tab.id && (
                        <i className="ri-arrow-right-s-line ml-auto text-blue-400"></i>
                      )}
                    </button>
                  ))}
                </nav>
              </div>
            </div>

            {/* Content Area */}
            <div className="lg:col-span-3 space-y-6">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 min-h-[600px]">

                {/* Tab General */}
                {activeTab === 'general' && (
                  <div className="space-y-8 animate-fade-in">
                    <div className="flex items-center gap-3 mb-6 pb-6 border-b border-gray-100">
                      <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center text-blue-600">
                        <i className="ri-settings-line text-2xl"></i>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">General</h3>
                        <p className="text-gray-500 text-sm">Configuración básica de la aplicación y localización.</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Nombre del Dispositivo
                        </label>
                        <input
                          type="text"
                          value={settings.general.deviceName}
                          onChange={(e) => handleSettingChange('general', 'deviceName', e.target.value)}
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Ubicación
                        </label>
                        <div className="relative">
                          <i className="ri-map-pin-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
                          <input
                            type="text"
                            value={settings.general.location}
                            onChange={(e) => handleSettingChange('general', 'location', e.target.value)}
                            className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Zona Horaria
                        </label>
                        <select
                          value={settings.general.timezone}
                          onChange={(e) => handleSettingChange('general', 'timezone', e.target.value)}
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                        >
                          <option value="Europe/Madrid">Europa/Madrid</option>
                          <option value="Europe/London">Europa/Londres</option>
                          <option value="America/New_York">América/Nueva York</option>
                          <option value="Asia/Tokyo">Asia/Tokio</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Idioma
                        </label>
                        <select
                          value={settings.general.language}
                          onChange={(e) => handleSettingChange('general', 'language', e.target.value)}
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                        >
                          <option value="es">Español</option>
                          <option value="en">English</option>
                          <option value="fr">Français</option>
                          <option value="de">Deutsch</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Intervalo de Actualización (segundos)
                        </label>
                        <div className="relative">
                          <i className="ri-timer-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
                          <input
                            type="number"
                            min="1"
                            max="60"
                            value={settings.general.updateInterval}
                            onChange={(e) => handleSettingChange('general', 'updateInterval', parseInt(e.target.value))}
                            className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                          />
                        </div>
                      </div>

                      <div className="flex items-center p-4 bg-blue-50 rounded-xl border border-blue-100">
                        <div className="flex-1">
                          <label htmlFor="autoRefresh" className="font-medium text-blue-900 block">
                            Actualización Automática
                          </label>
                          <p className="text-xs text-blue-700 mt-1">Mantener los datos siempre actualizados en tiempo real</p>
                        </div>
                        <div className="relative inline-block w-12 mr-2 align-middle select-none transition duration-200 ease-in">
                          <input
                            type="checkbox"
                            id="autoRefresh"
                            checked={settings.general.autoRefresh}
                            onChange={(e) => handleSettingChange('general', 'autoRefresh', e.target.checked)}
                            className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                            style={{ right: settings.general.autoRefresh ? '0' : 'auto', left: settings.general.autoRefresh ? 'auto' : '0', borderColor: settings.general.autoRefresh ? '#3B82F6' : '#E5E7EB' }}
                          />
                          <label htmlFor="autoRefresh" className={`toggle-label block overflow-hidden h-6 rounded-full cursor-pointer ${settings.general.autoRefresh ? 'bg-blue-500' : 'bg-gray-300'}`}></label>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Tab Notificaciones */}
                {activeTab === 'notifications' && (
                  <div className="space-y-8 animate-fade-in">
                    <div className="flex items-center gap-3 mb-6 pb-6 border-b border-gray-100">
                      <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center text-purple-600">
                        <i className="ri-notification-line text-2xl"></i>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">Notificaciones</h3>
                        <p className="text-gray-500 text-sm">Gestiona qué alertas quieres recibir y cómo.</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      {Object.entries(settings.notifications).map(([key, value]) => (
                        <div key={key} className="flex items-center justify-between p-5 bg-gray-50 rounded-xl border border-gray-100 hover:border-blue-200 transition-colors group">
                          <div className="flex items-start gap-4">
                            <div className={`mt-1 w-8 h-8 rounded-lg flex items-center justify-center ${value ? 'bg-blue-100 text-blue-600' : 'bg-gray-200 text-gray-500'}`}>
                              <i className={`text-lg ${key === 'emailAlerts' ? 'ri-mail-line' :
                                key === 'systemErrors' ? 'ri-error-warning-line' :
                                  key === 'maintenanceReminders' ? 'ri-tools-line' :
                                    key === 'performanceReports' ? 'ri-file-chart-line' :
                                      key === 'lowPerformance' ? 'ri-arrow-down-circle-line' :
                                        key === 'highTemperature' ? 'ri-temp-hot-line' : 'ri-wifi-off-line'
                                }`}></i>
                            </div>
                            <div>
                              <h4 className="font-bold text-gray-900 group-hover:text-blue-700 transition-colors">
                                {key === 'emailAlerts' && 'Alertas por Email'}
                                {key === 'systemErrors' && 'Errores del Sistema'}
                                {key === 'maintenanceReminders' && 'Recordatorios de Mantenimiento'}
                                {key === 'performanceReports' && 'Reportes de Rendimiento'}
                                {key === 'lowPerformance' && 'Bajo Rendimiento'}
                                {key === 'highTemperature' && 'Temperatura Alta'}
                                {key === 'gridDisconnection' && 'Desconexión de Red'}
                              </h4>
                              <p className="text-sm text-gray-500 mt-1">
                                {key === 'emailAlerts' && 'Recibir notificaciones por correo electrónico'}
                                {key === 'systemErrors' && 'Alertas cuando ocurran errores del sistema'}
                                {key === 'maintenanceReminders' && 'Recordatorios periódicos de mantenimiento'}
                                {key === 'performanceReports' && 'Reportes semanales de rendimiento'}
                                {key === 'lowPerformance' && 'Alertas cuando el rendimiento sea bajo'}
                                {key === 'highTemperature' && 'Alertas por temperatura elevada'}
                                {key === 'gridDisconnection' && 'Alertas por desconexión de la red'}
                              </p>
                            </div>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={value as boolean}
                              onChange={(e) => handleSettingChange('notifications', key, e.target.checked)}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Tab Umbrales */}
                {activeTab === 'thresholds' && (
                  <div className="space-y-8 animate-fade-in">
                    <div className="flex items-center gap-3 mb-6 pb-6 border-b border-gray-100">
                      <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center text-red-600">
                        <i className="ri-alarm-warning-line text-2xl"></i>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">Umbrales de Alerta</h3>
                        <p className="text-gray-500 text-sm">Define los límites operativos para generar alertas automáticas.</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="p-5 bg-gray-50 rounded-xl border border-gray-100">
                        <label className="block text-sm font-bold text-gray-900 mb-2 flex justify-between">
                          <span>Temperatura Máxima</span>
                          <span className="text-red-600">{settings.thresholds.maxTemperature}°C</span>
                        </label>
                        <input
                          type="range"
                          min="50"
                          max="100"
                          value={settings.thresholds.maxTemperature}
                          onChange={(e) => handleSettingChange('thresholds', 'maxTemperature', parseInt(e.target.value))}
                          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-red-500"
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-2">
                          <span>50°C</span>
                          <span>100°C</span>
                        </div>
                      </div>

                      <div className="p-5 bg-gray-50 rounded-xl border border-gray-100">
                        <label className="block text-sm font-bold text-gray-900 mb-2 flex justify-between">
                          <span>Eficiencia Mínima</span>
                          <span className="text-green-600">{settings.thresholds.minEfficiency}%</span>
                        </label>
                        <input
                          type="range"
                          min="70"
                          max="100"
                          value={settings.thresholds.minEfficiency}
                          onChange={(e) => handleSettingChange('thresholds', 'minEfficiency', parseInt(e.target.value))}
                          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-500"
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-2">
                          <span>70%</span>
                          <span>100%</span>
                        </div>
                      </div>

                      <div className="p-5 bg-gray-50 rounded-xl border border-gray-100">
                        <label className="block text-sm font-bold text-gray-900 mb-2 flex justify-between">
                          <span>Desbalance Máximo</span>
                          <span className="text-orange-600">{settings.thresholds.maxPowerImbalance}%</span>
                        </label>
                        <input
                          type="range"
                          min="5"
                          max="30"
                          value={settings.thresholds.maxPowerImbalance}
                          onChange={(e) => handleSettingChange('thresholds', 'maxPowerImbalance', parseInt(e.target.value))}
                          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-orange-500"
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-2">
                          <span>5%</span>
                          <span>30%</span>
                        </div>
                      </div>

                      <div className="p-5 bg-gray-50 rounded-xl border border-gray-100">
                        <label className="block text-sm font-bold text-gray-900 mb-4">Rango de Voltaje Seguro</label>
                        <div className="flex items-center gap-4">
                          <div className="flex-1">
                            <label className="text-xs text-gray-500 mb-1 block">Mínimo (V)</label>
                            <input
                              type="number"
                              min="200"
                              max="230"
                              value={settings.thresholds.lowVoltageThreshold}
                              onChange={(e) => handleSettingChange('thresholds', 'lowVoltageThreshold', parseInt(e.target.value))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-center font-medium"
                            />
                          </div>
                          <div className="text-gray-400">-</div>
                          <div className="flex-1">
                            <label className="text-xs text-gray-500 mb-1 block">Máximo (V)</label>
                            <input
                              type="number"
                              min="240"
                              max="260"
                              value={settings.thresholds.highVoltageThreshold}
                              onChange={(e) => handleSettingChange('thresholds', 'highVoltageThreshold', parseInt(e.target.value))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-center font-medium"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Tab Visualización */}
                {activeTab === 'display' && (
                  <div className="space-y-8 animate-fade-in">
                    <div className="flex items-center gap-3 mb-6 pb-6 border-b border-gray-100">
                      <div className="w-12 h-12 rounded-xl bg-pink-100 flex items-center justify-center text-pink-600">
                        <i className="ri-palette-line text-2xl"></i>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">Personalización Visual</h3>
                        <p className="text-gray-500 text-sm">Adapta la interfaz a tus preferencias.</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Tema de la Aplicación
                        </label>
                        <div className="grid grid-cols-3 gap-2">
                          {['light', 'dark', 'auto'].map((theme) => (
                            <button
                              key={theme}
                              onClick={() => handleSettingChange('display', 'theme', theme)}
                              className={`p-3 rounded-xl border flex flex-col items-center gap-2 transition-all ${settings.display.theme === theme
                                ? 'bg-blue-50 border-blue-200 text-blue-700 shadow-sm'
                                : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                                }`}
                            >
                              <i className={`text-xl ${theme === 'light' ? 'ri-sun-line' :
                                theme === 'dark' ? 'ri-moon-line' : 'ri-computer-line'
                                }`}></i>
                              <span className="text-xs font-medium capitalize">
                                {theme === 'light' ? 'Claro' : theme === 'dark' ? 'Oscuro' : 'Auto'}
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Dashboard Inicial
                        </label>
                        <select
                          value={settings.display.defaultDashboard}
                          onChange={(e) => handleSettingChange('display', 'defaultDashboard', e.target.value)}
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                        >
                          <option value="overview">Resumen General</option>
                          <option value="powerflow">Flujo de Energía</option>
                          <option value="inverter">Estado de Inversores</option>
                          <option value="energy">Medidor de Energía</option>
                        </select>
                      </div>

                      <div className="md:col-span-2 space-y-4">
                        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100">
                          <div>
                            <h4 className="font-medium text-gray-900">Animaciones en Gráficos</h4>
                            <p className="text-xs text-gray-500">Suavizar las transiciones de datos en tiempo real</p>
                          </div>
                          <div className="relative inline-block w-12 align-middle select-none transition duration-200 ease-in">
                            <input
                              type="checkbox"
                              id="chartAnimations"
                              checked={settings.display.chartAnimations}
                              onChange={(e) => handleSettingChange('display', 'chartAnimations', e.target.checked)}
                              className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                              style={{ right: settings.display.chartAnimations ? '0' : 'auto', left: settings.display.chartAnimations ? 'auto' : '0', borderColor: settings.display.chartAnimations ? '#3B82F6' : '#E5E7EB' }}
                            />
                            <label htmlFor="chartAnimations" className={`toggle-label block overflow-hidden h-6 rounded-full cursor-pointer ${settings.display.chartAnimations ? 'bg-blue-500' : 'bg-gray-300'}`}></label>
                          </div>
                        </div>

                        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100">
                          <div>
                            <h4 className="font-medium text-gray-900">Vista Compacta</h4>
                            <p className="text-xs text-gray-500">Reducir el espaciado para mostrar más información</p>
                          </div>
                          <div className="relative inline-block w-12 align-middle select-none transition duration-200 ease-in">
                            <input
                              type="checkbox"
                              id="compactView"
                              checked={settings.display.compactView}
                              onChange={(e) => handleSettingChange('display', 'compactView', e.target.checked)}
                              className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                              style={{ right: settings.display.compactView ? '0' : 'auto', left: settings.display.compactView ? 'auto' : '0', borderColor: settings.display.compactView ? '#3B82F6' : '#E5E7EB' }}
                            />
                            <label htmlFor="compactView" className={`toggle-label block overflow-hidden h-6 rounded-full cursor-pointer ${settings.display.compactView ? 'bg-blue-500' : 'bg-gray-300'}`}></label>
                          </div>
                        </div>

                        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100">
                          <div>
                            <h4 className="font-medium text-gray-900">Métricas Avanzadas</h4>
                            <p className="text-xs text-gray-500">Mostrar datos técnicos detallados (voltaje, amperaje, frecuencia)</p>
                          </div>
                          <div className="relative inline-block w-12 align-middle select-none transition duration-200 ease-in">
                            <input
                              type="checkbox"
                              id="showAdvancedMetrics"
                              checked={settings.display.showAdvancedMetrics}
                              onChange={(e) => handleSettingChange('display', 'showAdvancedMetrics', e.target.checked)}
                              className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                              style={{ right: settings.display.showAdvancedMetrics ? '0' : 'auto', left: settings.display.showAdvancedMetrics ? 'auto' : '0', borderColor: settings.display.showAdvancedMetrics ? '#3B82F6' : '#E5E7EB' }}
                            />
                            <label htmlFor="showAdvancedMetrics" className={`toggle-label block overflow-hidden h-6 rounded-full cursor-pointer ${settings.display.showAdvancedMetrics ? 'bg-blue-500' : 'bg-gray-300'}`}></label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Tab API */}
                {activeTab === 'api' && (
                  <div className="space-y-8 animate-fade-in">
                    <div className="flex items-center gap-3 mb-6 pb-6 border-b border-gray-100">
                      <div className="w-12 h-12 rounded-xl bg-cyan-100 flex items-center justify-center text-cyan-600">
                        <i className="ri-code-s-slash-line text-2xl"></i>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">Conexión API</h3>
                        <p className="text-gray-500 text-sm">Configura la conexión con tu dispositivo Fronius.</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Endpoint de API
                        </label>
                        <div className="relative">
                          <i className="ri-link text-gray-400 absolute left-4 top-1/2 -translate-y-1/2"></i>
                          <input
                            type="url"
                            value={settings.api.endpoint}
                            onChange={(e) => handleSettingChange('api', 'endpoint', e.target.value)}
                            className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all font-mono text-sm"
                            placeholder="http://192.168.1.100/solar_api/v1/"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Timeout (segundos)
                        </label>
                        <input
                          type="number"
                          min="5"
                          max="60"
                          value={settings.api.timeout}
                          onChange={(e) => handleSettingChange('api', 'timeout', parseInt(e.target.value))}
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Intentos de Reintento
                        </label>
                        <input
                          type="number"
                          min="1"
                          max="10"
                          value={settings.api.retryAttempts}
                          onChange={(e) => handleSettingChange('api', 'retryAttempts', parseInt(e.target.value))}
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                        />
                      </div>

                      <div className="md:col-span-2 flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100">
                        <div>
                          <h4 className="font-medium text-gray-900">Habilitar Logging de API</h4>
                          <p className="text-xs text-gray-500">Guardar registros de respuestas para depuración</p>
                        </div>
                        <div className="relative inline-block w-12 align-middle select-none transition duration-200 ease-in">
                          <input
                            type="checkbox"
                            id="enableLogging"
                            checked={settings.api.enableLogging}
                            onChange={(e) => handleSettingChange('api', 'enableLogging', e.target.checked)}
                            className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                            style={{ right: settings.api.enableLogging ? '0' : 'auto', left: settings.api.enableLogging ? 'auto' : '0', borderColor: settings.api.enableLogging ? '#3B82F6' : '#E5E7EB' }}
                          />
                          <label htmlFor="enableLogging" className={`toggle-label block overflow-hidden h-6 rounded-full cursor-pointer ${settings.api.enableLogging ? 'bg-blue-500' : 'bg-gray-300'}`}></label>
                        </div>
                      </div>
                    </div>

                    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
                      <div className="flex items-start space-x-3">
                        <i className="ri-information-line text-amber-600 text-xl mt-0.5"></i>
                        <div>
                          <h4 className="text-amber-800 font-bold">Información de Conexión</h4>
                          <p className="text-amber-700 text-sm mt-1 leading-relaxed">
                            Asegúrate de que la dirección IP del dispositivo Fronius sea correcta y que esté en la misma red.
                            Los endpoints típicos son: <code className="bg-amber-100 px-1 rounded text-amber-900">GetPowerFlowRealtimeData.fcgi</code>, <code className="bg-amber-100 px-1 rounded text-amber-900">GetInverterRealtimeData.cgi</code>.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}